// src/App.js

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Container, Nav, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { ethers } from 'ethers';
import { connectWallet, getWeb3Modal } from './utils/wallet';
import { getMainnetProvider, getBaseProvider, getEtherlinkProvider } from './utils/providers';
import MyEarnings from './pages/MyEarnings';

import MyNFTCollection from './contracts/MyNFTCollection.json';
import AdminSection from './components/AdminSection';
import NFTGallery from './components/galleries/NFTGallery';
import DocsPage from './pages/DocsPage';
import FastWorkersMovedView from './components/views/FastWorkersMovedView';

import { encryptPrivateKey, decryptPrivateKey } from './utils/encryption';

import {
  processDeposit,
  processWithdraw,
  processWorkerWithdraw
} from './services/tokenService';
import TopNav from './components/navigation/TopNav';
import SogniAuthModals from './components/modals/SogniAuthModals';
import { redirectToAuth } from './utils/auth';

import SogniConsumerFundManagerABI from './contracts/SogniConsumerFundManager.json';
import { useCurrentAccount, useSogniClient } from "./utils/sogniClient";
import confetti from 'canvas-confetti';

const contractAddress = process.env.REACT_APP_DEPLOYED_NFT_CONTRACT || '';
const adminAddress = (process.env.REACT_APP_SIGNER_ADDRESS || '').toLowerCase();
const baseURI = process.env.REACT_APP_INIT_BASE_URI || '';
const desiredChainIdDecimal = parseInt(process.env.REACT_APP_CHAIN_ID || '0', 10);
const desiredChainIdHex = '0x' + desiredChainIdDecimal.toString(16);
const SOGNI_CONSUMER_FUND_MANAGER_ADDRESS =
  process.env.REACT_APP_SOGNI_CONSUMER_FUND_MANAGER_ADDRESS;
const TSOGNI_TOKEN_ADDRESS = process.env.REACT_APP_SOGNI_ERC20;
const rpcUrl = process.env.REACT_APP_RPC_URL || '';

const publicProvider = getBaseProvider();
const publicContract = new ethers.Contract(
  contractAddress,
  MyNFTCollection.abi,
  publicProvider
);

// Etherlink read-only contract (blacklist checks, etc.) — null when RPC not configured
const etherlinkNftAddress = process.env.REACT_APP_ETHERLINK_NFT_CONTRACT || '';
const etherlinkPublicProvider = getEtherlinkProvider();
const etherlinkPublicContract = (etherlinkNftAddress && etherlinkPublicProvider)
  ? new ethers.Contract(etherlinkNftAddress, MyNFTCollection.abi, etherlinkPublicProvider)
  : null;

const mainnetProvider = getMainnetProvider();

const App = () => {
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('explorer');
  const location = useLocation();
  const navigate = useNavigate();

  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [contract, setContract] = useState(null);
  const [account, setAccount] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [refreshGallery, setRefreshGallery] = useState(false);
  const [confirmedAdminMints, setConfirmedAdminMints] = useState({});

  // Keep the already-mounted gallery authoritative immediately after an admin
  // mint. The NFT API indexer may not have processed the Transfer event yet.
  const handleAdminMintSuccess = useCallback(({ tokenIds, owner }) => {
    if (!Array.isArray(tokenIds) || !owner) return;
    setConfirmedAdminMints(previous => {
      const next = { ...previous };
      tokenIds.forEach(id => { next[id] = owner; });
      return next;
    });
    setRefreshGallery(previous => !previous);
  }, []);

  // --- Sogni/Authentication state ---
  const [isSogniLoggedIn, setIsSogniLoggedIn] = useState(false);
  const sogniClient = useSogniClient();
  const sogniClientReady = !!sogniClient;
  const currentAccount = useCurrentAccount(sogniClient);

  // Legacy token state removed (cookie auth)
  const [sogniUsername, setSogniUsername] = useState('');
  const [sogniPassword, setSogniPassword] = useState('');
  const [sogniErrorMessage, setSogniErrorMessage] = useState('');
  const [showSogniLoginModal, setShowSogniLoginModal] = useState(false);

  // "Reauth" Modal
  const [showSogniReauthModal, setShowSogniReauthModal] = useState(false);
  const [sogniReauthPassword, setSogniReauthPassword] = useState('');

  // Balances for MyEarnings
  const [sogniBalance, setSogniBalance] = useState('0');
  const [sogniManagerBalance, setSogniManagerBalance] = useState('0');
  const [workerUnclaimedBalance, setWorkerUnclaimedBalance] = useState(0);

  // Backup Private Key modals
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [backupPassword, setBackupPassword] = useState('');
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [privateKeyToShow, setPrivateKeyToShow] = useState('');

  // Deposit/Withdraw/Send modals
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showWorkerWithdrawModal, setShowWorkerWithdrawModal] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [workerWithdrawAmount, setWorkerWithdrawAmount] = useState('');
  const [transactionStatus, setTransactionStatus] = useState('');
  const [transactionHash, setTransactionHash] = useState('');
  const [sendRecipient, setSendRecipient] = useState('');
  const [sendAmount, setSendAmount] = useState('');
  const [sendStatus, setSendStatus] = useState('');
  const [sendTxHash, setSendTxHash] = useState('');
  const [sending, setSending] = useState(false);

  // Track if user has "just created" a Sogni account
  const [accountCreated, setAccountCreated] = useState(false);

  // Bridge-in-progress flag — suppresses "switch back to Base" alert during bridging
  const [bridgeInProgress, setBridgeInProgress] = useState(false);

  // Pending post-reauth action (e.g., resume transfer)
  const pendingActionRef = useRef(null);

  // Session restoration handled via client auth events

  // -------------------------------------------------------------------
  // On mount, auto-connect wallet if cached (without triggering UI)
  // -------------------------------------------------------------------
  useEffect(() => {
    const web3Modal = getWeb3Modal();
    if (web3Modal.cachedProvider) {
      (async () => {
        try {
          // Attempt silent connection without triggering wallet UI
          const instance = await web3Modal.connectTo(web3Modal.cachedProvider);
          const connectedProvider = new ethers.providers.Web3Provider(instance, 'any');
          const connectedSigner = connectedProvider.getSigner();
          const address = await connectedSigner.getAddress();
          setProvider(connectedProvider);
          setSigner(connectedSigner);
          setAccount(address);
          setIsAdmin(address.toLowerCase() === adminAddress);

          const { chainId } = await connectedProvider.getNetwork();
          if (chainId !== desiredChainIdDecimal) {
            try {
              await connectedProvider.provider.request({
                method: 'wallet_switchEthereumChain',
                params: [{ chainId: desiredChainIdHex }]
              });
              // Dynamically update
              const updatedNetwork = await connectedProvider.getNetwork();
              if (updatedNetwork.chainId === desiredChainIdDecimal) {
                const updatedSigner = connectedProvider.getSigner();
                setSigner(updatedSigner);
                const nftContract = new ethers.Contract(
                  contractAddress,
                  MyNFTCollection.abi,
                  updatedSigner
                );
                setContract(nftContract);
              } else {
                throw new Error('Chain switch failed');
              }
            } catch (switchError) {
              if (switchError.code === 4902) {
                try {
                  await connectedProvider.provider.request({
                    method: 'wallet_addEthereumChain',
                    params: [
                      {
                        chainId: desiredChainIdHex,
                        chainName: 'Base',
                        rpcUrls: [rpcUrl],
                        blockExplorerUrls: ['https://basescan.org'],
                        nativeCurrency: {
                          name: 'Ethereum',
                          symbol: 'ETH',
                          decimals: 18
                        }
                      }
                    ]
                  });
                  await connectedProvider.provider.request({
                    method: 'wallet_switchEthereumChain',
                    params: [{ chainId: desiredChainIdHex }]
                  });
                  const updatedNetwork = await connectedProvider.getNetwork();
                  if (updatedNetwork.chainId === desiredChainIdDecimal) {
                    const updatedSigner = connectedProvider.getSigner();
                    setSigner(updatedSigner);
                    const nftContract = new ethers.Contract(
                      contractAddress,
                      MyNFTCollection.abi,
                      updatedSigner
                    );
                    setContract(nftContract);
                  }
                } catch (addError) {
                  console.error('Failed to add network:', addError);
                  await web3Modal.clearCachedProvider();
                  setAccount('');
                  setSigner(null);
                  setIsAdmin(false);
                  setContract(null);
                }
              } else {
                console.error('Failed to switch network:', switchError);
                await web3Modal.clearCachedProvider();
                setAccount('');
                setSigner(null);
                setIsAdmin(false);
                setContract(null);
              }
            }
          }

          // Listen for account changes
          connectedProvider.provider.on('accountsChanged', (accounts) => {
            if (accounts.length === 0) {
              setAccount('');
              setSigner(null);
              setIsAdmin(false);
              setContract(null);
            } else {
              setAccount(accounts[0]);
              setIsAdmin(accounts[0].toLowerCase() === adminAddress);
              const newSigner = connectedProvider.getSigner();
              setSigner(newSigner);
              const nftContract = new ethers.Contract(
                contractAddress,
                MyNFTCollection.abi,
                newSigner
              );
              setContract(nftContract);
            }
          });

          // Listen for chain changes
          connectedProvider.provider.on('chainChanged', async (_chainId) => {
            const updatedNetwork = await connectedProvider.getNetwork();
            if (updatedNetwork.chainId === desiredChainIdDecimal) {
              const updatedSigner = connectedProvider.getSigner();
              setSigner(updatedSigner);
              const nftContract = new ethers.Contract(
                contractAddress,
                MyNFTCollection.abi,
                updatedSigner
              );
              setContract(nftContract);
            } else if (!bridgeInProgress) {
              alert('Network changed. Please switch back to Base.');
            }
          });
        } catch (err) {
          console.error('Failed to auto-connect wallet:', err);
          await web3Modal.clearCachedProvider();
          setAccount('');
          setSigner(null);
          setIsAdmin(false);
          setContract(null);
        }
      })();
    }
  }, []);

  // Keep login state in sync with SogniClient currentAccount
  useEffect(() => {
    if (!sogniClientReady) return;
    if (currentAccount) {
      // isAuthenicated may be true before username is populated;
      // fall back to sessionStorage for display name
      setSogniUsername(currentAccount.username || sessionStorage.getItem('sogni-username') || '');
      setIsSogniLoggedIn(true);
    } else {
      setIsSogniLoggedIn(false);
    }
  }, [sogniClientReady, currentAccount]);

  // -------------------------------------------------------------------
  // If signer => set contract
  // -------------------------------------------------------------------
  useEffect(() => {
    if (signer) {
      const nftContract = new ethers.Contract(
        contractAddress,
        MyNFTCollection.abi,
        signer
      );
      setContract(nftContract);
    }
  }, [signer]);

  // -------------------------------------------------------------------
  // If path changes => set "view"
  // -------------------------------------------------------------------
  useEffect(() => {
    const path = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const section = searchParams.get('section');
    
    // Handle redirects from /analytics?section=gpu to /fastworkers
    if (path === '/analytics' && section === 'gpu') {
      // Preserve all query parameters except section
      const newParams = new URLSearchParams(location.search);
      newParams.delete('section');
      const queryString = newParams.toString();
      const newPath = `/fastworkers${queryString ? `?${queryString}` : ''}`;
      navigate(newPath, { replace: true });
      return;
    }
    
    if (path === '/mynfts') {
      setView('myNFTs');
    } else if (path === '/analytics') {
      setView('analytics');
    } else if (path === '/fastworkers') {
      setView('fastworkers');
    } else if (path === '/myearnings') {
      setView('myEarnings');
    } else if (path === '/docs') {
      setView('docs');
    } else {
      setView('explorer');
    }
  }, [location.pathname, location.search, navigate]);

  // -------------------------------------------------------------------
  // Sogni signup callback (cookies auth)
  // -------------------------------------------------------------------
  useEffect(() => {
    const currentPath = location.pathname.toLowerCase();
    if (currentPath === '/callback') {
      const params = new URLSearchParams(location.search);
      const usernameParam = params.get('username');
      if (usernameParam && sogniClientReady && sogniClient) {
        // Mark them as signed up and prompt login to set cookie/session
        setSogniUsername(usernameParam);
        setIsSogniLoggedIn(false);
        setAccountCreated(true);
        navigate('/?filter=AVAILABLE', { replace: true });
        setShowSogniLoginModal(true);
      }
    }
  }, [location, sogniClientReady, sogniClient, navigate]);

  // -------------------------------------------------------------------
  // Connect Wallet
  // -------------------------------------------------------------------
  const handleConnectWallet = async () => {
    try {
      const { provider: connectedProvider, signer: connectedSigner } = await connectWallet();
      setProvider(connectedProvider);
      setSigner(connectedSigner);
      const address = await connectedSigner.getAddress();
      setAccount(address);
      setIsAdmin(address.toLowerCase() === adminAddress);

      const { chainId } = await connectedProvider.getNetwork();
      if (chainId !== desiredChainIdDecimal) {
        try {
          await connectedProvider.provider.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: desiredChainIdHex }]
          });
          // No reload; dynamically update
          const updatedNetwork = await connectedProvider.getNetwork();
          if (updatedNetwork.chainId === desiredChainIdDecimal) {
            // Re-init signer and contract if needed
            const updatedSigner = connectedProvider.getSigner();
            setSigner(updatedSigner);
            const nftContract = new ethers.Contract(
              contractAddress,
              MyNFTCollection.abi,
              updatedSigner
            );
            setContract(nftContract);
          } else {
            throw new Error('Chain switch failed');
          }
        } catch (switchError) {
          if (switchError.code === 4902) {
            // Add chain
            try {
              await connectedProvider.provider.request({
                method: 'wallet_addEthereumChain',
                params: [
                  {
                    chainId: desiredChainIdHex,
                    chainName: 'Base',
                    rpcUrls: [rpcUrl],
                    blockExplorerUrls: ['https://basescan.org'],
                    nativeCurrency: {
                      name: 'Ethereum',
                      symbol: 'ETH',
                      decimals: 18
                    }
                  }
                ]
              });
              await connectedProvider.provider.request({
                method: 'wallet_switchEthereumChain',
                params: [{ chainId: desiredChainIdHex }]
              });
              // Dynamically update as above
              const updatedNetwork = await connectedProvider.getNetwork();
              if (updatedNetwork.chainId === desiredChainIdDecimal) {
                const updatedSigner = connectedProvider.getSigner();
                setSigner(updatedSigner);
                const nftContract = new ethers.Contract(
                  contractAddress,
                  MyNFTCollection.abi,
                  updatedSigner
                );
                setContract(nftContract);
              }
            } catch (addError) {
              console.error('Failed to add network:', addError);
              alert('Failed to add network. Please try manually.');
            }
          } else {
            console.error('Failed to switch network:', switchError);
            alert('Failed to switch network. Please try manually.');
          }
        }
      }

      // Listen for account changes
      connectedProvider.provider.on('accountsChanged', (accounts) => {
        if (accounts.length === 0) {
          setAccount('');
          setSigner(null);
          setIsAdmin(false);
          setContract(null);
        } else {
          setAccount(accounts[0]);
          setIsAdmin(accounts[0].toLowerCase() === adminAddress);
          const newSigner = connectedProvider.getSigner();
          setSigner(newSigner);
          const nftContract = new ethers.Contract(
            contractAddress,
            MyNFTCollection.abi,
            newSigner
          );
          setContract(nftContract);
        }
      });

      // Listen for chain changes
      connectedProvider.provider.on('chainChanged', async (_chainId) => {
        // Dynamically handle without reload
        const updatedNetwork = await connectedProvider.getNetwork();
        if (updatedNetwork.chainId === desiredChainIdDecimal) {
          const updatedSigner = connectedProvider.getSigner();
          setSigner(updatedSigner);
          const nftContract = new ethers.Contract(
            contractAddress,
            MyNFTCollection.abi,
            updatedSigner
          );
          setContract(nftContract);
        } else if (!bridgeInProgress) {
          alert('Network changed. Please switch back to Base.');
        }
      });
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      alert('Failed to connect wallet: ' + error.message);
    }
  };

  // -------------------------------------------------------------------
  // Disconnect
  // -------------------------------------------------------------------
  const handleDisconnectWallet = async () => {
    if (sogniClient && isSogniLoggedIn) {
      try {
        await sogniClient.account.logout();
      } catch (err) {
        console.warn('Sogni logout failed:', err);
      }
    }

    // Clear session username and local key
    sessionStorage.removeItem('sogni-username');
    localStorage.removeItem('encryptedSogniPrivateKey');
    
    // Disconnect Web3Modal
    const web3Modal = getWeb3Modal();
    await web3Modal.clearCachedProvider();

    if (provider && provider.provider && provider.provider.disconnect) {
      await provider.provider.disconnect();
    }

    setAccount('');
    setSigner(null);
    setIsAdmin(false);
    setContract(null);
    setIsSogniLoggedIn(false);
  };

  // -------------------------------------------------------------------
  // Sogni Login
  // -------------------------------------------------------------------
  const handleSogniLogin = () => {
    // Mirror photobooth: redirect to centralized auth
    redirectToAuth('login');
  };

  const handleSogniLogout = async () => {
    try {
      if (sogniClient) {
        await sogniClient.account.logout();
      }
    } catch (err) {
      console.warn('Sogni logout failed:', err);
    }
    sessionStorage.removeItem('sogni-username');
    setIsSogniLoggedIn(false);
  };

  // Used by gallery actions (e.g., Transfer) to prompt for Sogni password
  const requestSogniReauth = (afterSuccess) => {
    // Pre-fill username from session if missing
    if (!sogniUsername && sogniClient?.account?.currentAccount?.username) {
      setSogniUsername(sogniClient.account.currentAccount.username);
    }
    setSogniErrorMessage('');
    setSogniReauthPassword('');
    if (afterSuccess && typeof afterSuccess === 'function') {
      pendingActionRef.current = afterSuccess;
    }
    setShowSogniReauthModal(true);
  };

  const handleSogniLoginSubmit = async (e) => {
    e.preventDefault();
    setSogniErrorMessage('');

    try {
      if (!sogniClient || !sogniClientReady) {
        throw new Error('SogniClient not initialized yet.');
      }

      // 1) Login to Sogni (sets HttpOnly cookie)
      await sogniClient.account.login(sogniUsername, sogniPassword);

      sessionStorage.setItem('sogni-username', sogniUsername);

      // 2) Retrieve Sogni wallet
      const sogniWallet = sogniClient.account.getWallet(sogniUsername, sogniPassword);
      const privateKey = sogniWallet.privateKey;

      // 3) Create local Ethers wallet
      const providerInstance = new ethers.providers.JsonRpcProvider(rpcUrl);
      const connectedWallet = new ethers.Wallet(privateKey, providerInstance);
      const address = await connectedWallet.getAddress();

      // 4) Store encrypted private key for long-term storage
      const encryptedKey = encryptPrivateKey(privateKey, sogniPassword);
      localStorage.setItem('encryptedSogniPrivateKey', encryptedKey);

      // 5) Update app state
      setProvider(providerInstance);
      setSigner(connectedWallet);
      setAccount(address);
      setIsSogniLoggedIn(true);

      setShowSogniLoginModal(false);
      setSogniPassword('');
    } catch (error) {
      console.error('Sogni login failed:', error);
      setSogniErrorMessage(error.message);
      // Clear session data on failure
      sessionStorage.removeItem('sogni-username');
      localStorage.removeItem('encryptedSogniPrivateKey');
      alert('Sogni login failed: ' + error.message);
    }
  };

  // -------------------------------------------------------------------
  // Reauth if user had an encrypted key
  // -------------------------------------------------------------------
  const handleSogniReauthSubmit = async (e) => {
    e.preventDefault();
    setSogniErrorMessage('');

    const encryptedKey = localStorage.getItem('encryptedSogniPrivateKey');
    const effectiveUsername = sessionStorage.getItem('sogni-username') || sogniUsername;
    if (!sogniUsername && effectiveUsername) {
      setSogniUsername(effectiveUsername);
    }

    try {
      if (!effectiveUsername) {
        throw new Error('No Sogni username found. Please log in again.');
      }

      await sogniClient.account.login(effectiveUsername, sogniReauthPassword);

      let privateKey;
      if (encryptedKey) {
        const decryptedKey = decryptPrivateKey(encryptedKey, sogniReauthPassword);
        privateKey = decryptedKey;
      } else {
        // Fallback: derive from Sogni wallet using the provided password
        const sogniWallet = sogniClient.account.getWallet(effectiveUsername, sogniReauthPassword);
        privateKey = sogniWallet?.privateKey;
        if (privateKey) {
          // Cache encrypted key for future fast reauth
          const saved = encryptPrivateKey(privateKey, sogniReauthPassword);
          localStorage.setItem('encryptedSogniPrivateKey', saved);
        }
      }

      if (!privateKey) {
        alert('Incorrect password. Please try again.');
        return;
      }

      const providerInstance = new ethers.providers.JsonRpcProvider(rpcUrl);
      const connectedWallet = new ethers.Wallet(privateKey, providerInstance);
      const address = await connectedWallet.getAddress();
      const nftContract = new ethers.Contract(
        contractAddress,
        MyNFTCollection.abi,
        connectedWallet
      );

      setProvider(providerInstance);
      setSigner(connectedWallet);
      setAccount(address);
      setContract(nftContract);
      setIsSogniLoggedIn(true);

      setShowSogniReauthModal(false);
      setSogniReauthPassword('');

      // Execute any pending action queued before reauth.
      // Defer to next tick so React flushes the state updates above
      // (account, contract, signer) before the action reads them.
      const pendingAction = pendingActionRef.current;
      pendingActionRef.current = null;
      if (pendingAction) {
        setTimeout(async () => {
          try { await pendingAction(); } catch (e) { console.error('Pending action failed:', e); }
        }, 0);
      }
    } catch (error) {
      console.error('Reauthentication failed:', error);
      alert('Reauthentication failed. Incorrect password or no valid session could be established.');
    }
  };

  const handleSogniReauthCancel = () => {
    localStorage.removeItem('encryptedSogniPrivateKey');
    sessionStorage.removeItem('sogni-username');
    pendingActionRef.current = null;
    setSogniReauthPassword('');
    setSogniErrorMessage('');
    setShowSogniReauthModal(false);
  };

  // -------------------------------------------------------------------
  // If on MyEarnings page => fetch balances
  // -------------------------------------------------------------------
  useEffect(() => {
    const fetchSogniBalance = async () => {
      if (account && signer && view === 'myEarnings' && TSOGNI_TOKEN_ADDRESS) {
        try {
          const erc20Abi = ['function balanceOf(address) view returns (uint256)'];
          const sogniTokenContract = new ethers.Contract(TSOGNI_TOKEN_ADDRESS, erc20Abi, signer);
          const balance = await sogniTokenContract.balanceOf(account);
          setSogniBalance(ethers.utils.formatEther(balance));
        } catch (error) {
          console.error('Failed to fetch SOGNI balance:', error);
          alert('Failed to fetch SOGNI balance.');
        }
      } else if (view !== 'myEarnings') {
        setSogniBalance('0');
      }
    };
    fetchSogniBalance();
  }, [account, signer, view]);

  // SogniConsumerFundManager balance
  useEffect(() => {
    const fetchSogniManagerBalance = async () => {
      if (
        view === 'myEarnings' &&
        isSogniLoggedIn &&
        account &&
        provider &&
        SOGNI_CONSUMER_FUND_MANAGER_ADDRESS
      ) {
        try {
          const c = new ethers.Contract(
            SOGNI_CONSUMER_FUND_MANAGER_ADDRESS,
            SogniConsumerFundManagerABI.abi,
            provider
          );
          let balance = await c.balanceOf(account);
          setSogniManagerBalance(ethers.utils.formatEther(balance));
        } catch (error) {
          console.error('Failed to fetch SogniConsumerFundManager balance:', error);
          alert('Failed to fetch deposit balance.');
        }
      } else if (view !== 'myEarnings') {
        setSogniManagerBalance('0');
      }
    };
    fetchSogniManagerBalance();
  }, [view, isSogniLoggedIn, account, provider]);

  // On-Chain Worker Balance
  useEffect(() => {
    const fetchOnchainBalance = async () => {
      if (!sogniClient || !isSogniLoggedIn) return;
      try {
        const walletAddress = sogniClient.account.currentAccount.walletAddress;
        if (!walletAddress) {
          console.warn('No wallet address found');
          return;
        }

        const response = await sogniClient.apiClient.rest.get(
          '/v3/account/balance/onchain',
          {
            walletAddress: walletAddress
          }
        );
        if (response.status === 'success') {
          setWorkerUnclaimedBalance(
            response.data.balance?.unclaimed || 0
          );
        }
      } catch (err) {
        console.error('Failed to fetch onchain balance:', err);
        alert('Failed to fetch worker balance.');
      }
    };

    if (view === 'myEarnings') {
      fetchOnchainBalance();
    }
  }, [sogniClient, isSogniLoggedIn, view]);

  // -------------------------------------------------------------------
  // Deposit, Withdraw, Send
  // -------------------------------------------------------------------
  const openDepositModal = () => {
    if (!sogniBalance || isNaN(parseFloat(sogniBalance))) {
      alert('Unable to determine wallet balance.');
      return;
    }
    setDepositAmount(sogniBalance);
    setTransactionStatus('');
    setTransactionHash('');
    setShowDepositModal(true);
  };

  const openWithdrawModal = () => {
    if (!sogniManagerBalance || isNaN(parseFloat(sogniManagerBalance))) {
      alert('Unable to determine deposit balance.');
      return;
    }
    setWithdrawAmount(sogniManagerBalance);
    setTransactionStatus('');
    setTransactionHash('');
    setShowWithdrawModal(true);
  };

  const openWorkerClaimModal = () => {
    if (isNaN(parseFloat(workerUnclaimedBalance))) {
      alert('Unable to determine worker unclaimed balance.');
      return;
    } else if (workerUnclaimedBalance < 1000) {
      alert('You must have at least 1,000 SOGNI in your balance to claim.');
      return;
    }
    setWorkerWithdrawAmount(workerUnclaimedBalance);
    setShowWorkerWithdrawModal(true);
  };

  const openSendModal = () => {
    if (!sogniBalance || isNaN(parseFloat(sogniBalance))) {
      alert('Unable to determine wallet balance.');
      return;
    }
    setSendRecipient('');
    setSendAmount(sogniBalance);
    setSendStatus('');
    setSendTxHash('');
    setShowSendModal(true);
  };

  const handleConfirmDeposit = async () => {
    if (parseFloat(depositAmount) <= 0 || parseFloat(depositAmount) > parseFloat(sogniBalance)) {
      setTransactionStatus('Invalid deposit amount.');
      return;
    }
    if (!signer || !account) {
      return requestSogniReauth(async () => {
        await handleConfirmDeposit();
      });
    }
    setTransactionStatus('Processing deposit...');
    setTransactionHash('');
    try {
      const result = await processDeposit(
        signer,
        account,
        depositAmount,
        desiredChainIdDecimal
      );
      if (result && result.txHash) {
        setTransactionHash(result.txHash);
      }
      setTransactionStatus(`Deposit successful!`);
    } catch (err) {
      console.error('Deposit error:', err);
      setTransactionStatus(`Error: ${err.message}`);
    }
  };

  const handleConfirmWithdraw = async () => {
    if (parseFloat(withdrawAmount) <= 0 || parseFloat(withdrawAmount) > parseFloat(sogniManagerBalance)) {
      setTransactionStatus('Invalid withdraw amount.');
      return;
    }
    if (!signer || !account) {
      return requestSogniReauth(async () => {
        await handleConfirmWithdraw();
      });
    }
    setTransactionStatus('Processing withdrawal...');
    setTransactionHash('');
    try {
      const result = await processWithdraw(
        signer,
        account,
        withdrawAmount,
        desiredChainIdDecimal
      );
      if (result && result.txHash) {
        setTransactionHash(result.txHash);
      }
      setTransactionStatus(`Withdraw successful!`);
    } catch (err) {
      console.error(err);
      setTransactionStatus(`Error: ${err.message}`);
    }
  };

  const handleSendTsogni = async (setSendFormError) => {
    if (parseFloat(sendAmount) <= 0 || parseFloat(sendAmount) > parseFloat(sogniBalance)) {
      if (setSendFormError) setSendFormError('Invalid send amount.');
      return;
    }
    if (!ethers.utils.isAddress(sendRecipient.trim())) {
      if (setSendFormError) setSendFormError('Invalid recipient address.');
      return;
    }
    if (!signer || !account) {
      return requestSogniReauth(async () => {
        await handleSendTsogni(setSendFormError);
      });
    }
    setSendStatus('Processing send...');
    setSendTxHash('');
    setSending(true);
    if (setSendFormError) setSendFormError('');

    try {

      let recipientAddress = sendRecipient.trim();
      if (!ethers.utils.isAddress(recipientAddress)) {
        // Try ENS
        const resolvedENS = await getMainnetProvider().resolveName(recipientAddress);
        if (resolvedENS) {
          recipientAddress = resolvedENS;
        } else {
          // Try Sogni username
          try {
            const axiosImport = (await import('axios')).default;
            const resp = await axiosImport.get(
              `https://api.sogni.ai/v1/account/username-to-address?username=${recipientAddress}`
            );
            if (
              resp.data?.status === 'success' &&
              resp.data.data?.address &&
              ethers.utils.isAddress(resp.data.data.address)
            ) {
              recipientAddress = resp.data.data.address;
            } else {
              throw new Error('Invalid Sogni username.');
            }
          } catch (err) {
            if (setSendFormError) {
              setSendFormError(
                'Invalid recipient. Please enter a valid address, ENS name, or Sogni username.'
              );
            }
            setSendStatus('');
            setSending(false);
            return;
          }
        }
      }

      const erc20Abi = ['function transfer(address to, uint256 amount) returns (bool)'];
      const tokenContract = new ethers.Contract(TSOGNI_TOKEN_ADDRESS, erc20Abi, signer);
      const amountToSend = ethers.utils.parseEther(sendAmount);

      const gasPrice = await signer.getGasPrice();
      const tx = await tokenContract.transfer(recipientAddress, amountToSend, {
        gasLimit: 100000,
        gasPrice: gasPrice
      });

      const receipt = await tx.wait();
      setSendTxHash(receipt.transactionHash);
      setSendStatus('Send successful!');
    } catch (err) {
      console.error('Send error:', err);
      setSendStatus(`Error: ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleConfirmWorkerWithdraw = async (destination) => {
    if (parseFloat(workerWithdrawAmount) <= 0 || parseFloat(workerWithdrawAmount) > workerUnclaimedBalance) {
      setTransactionStatus('Invalid withdraw amount.');
      return;
    }
    if (!signer || !account) {
      return requestSogniReauth(async () => {
        await handleConfirmWorkerWithdraw(destination);
      });
    }
    setTransactionStatus('Processing worker withdrawal...');
    setTransactionHash('');
    try {

      const result = await processWorkerWithdraw(
        signer,
        account,
        workerWithdrawAmount.toString(),
        destination,
        desiredChainIdDecimal
      );
      if (result && result.txHash) {
        setTransactionHash(result.txHash);
      }
      setTransactionStatus(`Worker withdraw successful!`);

      confetti({
        particleCount: 200,
        spread: 90,
        origin: { y: 0.6 }
      });
      setTimeout(() => {
        confetti({
          particleCount: 200,
          spread: 120,
          origin: { x: 1, y: 0.6 }
        });
      }, 400);
      setTimeout(() => {
        confetti({
          particleCount: 200,
          spread: 120,
          origin: { x: 0, y: 0.6 }
        });
      }, 800);
    } catch (err) {
      console.error('Worker withdraw error:', err);
      setTransactionStatus(`Error: ${err.message}`);
    }
  };

  // -------------------------------------------------------------------
  // BACKUP PRIVATE KEY
  // -------------------------------------------------------------------
  const handleBackupPrivateKeyEvent = async (e) => {
    e.preventDefault();
    try {
      if (!sogniClient || !sogniClientReady) {
        alert('SogniClient not ready. Please wait and try again.');
        return;
      }
      if (!sogniUsername) {
        alert('No Sogni username found. Please log in with Sogni first.');
        return;
      }

      await sogniClient.account.login(sogniUsername, backupPassword);

      const sogniWallet = sogniClient.account.getWallet(sogniUsername, backupPassword);
      const privateKey = sogniWallet.privateKey;
      if (!privateKey) {
        alert('Failed to retrieve private key. Double-check your password.');
        return;
      }

      setPrivateKeyToShow(privateKey);
      setShowPrivateKey(true);
    } catch (err) {
      console.error('Reauthentication failed:', err);
      alert('Reauthentication failed. Incorrect password or Sogni not available.');
    }
  };

  // -------------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------------
  return (
    <Container
      fluid
      style={{ backgroundColor: '#000', color: '#fff', minHeight: '100vh', padding: '0' }}
    >
      <TopNav
        account={account}
        handleDisconnectWallet={handleDisconnectWallet}
        handleConnectWallet={handleConnectWallet}
        handleSogniLogin={handleSogniLogin}
        handleSogniLogout={handleSogniLogout}
        sogniClientReady={sogniClientReady}
        isSogniLoggedIn={isSogniLoggedIn}
        sogniUsername={sogniUsername}
      />

      <Container style={{ padding: '20px' }}>
        {isAdmin && contract && (
          <AdminSection
            contract={contract}
            account={account}
            onRequireLogin={requestSogniReauth}
            onMintSuccess={handleAdminMintSuccess}
          />
        )}

        {view === 'explorer' && (
          <p>You can pick an NFT to mint by selecting it from the gallery below.</p>
        )}

        <Nav
          variant="tabs"
          activeKey={view}
          style={{ marginBottom: '20px', marginTop: '10px' }}
        >
          <Nav.Item>
            <Nav.Link eventKey="explorer" onClick={() => navigate('/')}>
              NFT Collections Explorer
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="myNFTs" onClick={() => navigate('/mynfts')}>
              My NFTs
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="myEarnings" onClick={() => navigate('/myearnings')}>
              My Earnings
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="analytics" onClick={() => navigate('/analytics')}>
              Analytics
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="fastworkers" onClick={() => navigate('/fastworkers')}>
              My Fast Workers
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="docs" onClick={() => navigate('/docs')}>
              Docs
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {loading ? (
          <div className="text-center">
            <Spinner animation="border" variant="light" />
          </div>
        ) : view === 'analytics' || view === 'myNFTs' || view === 'explorer' ? (
          <NFTGallery
            baseURI={baseURI}
            contract={contract}
            publicContract={publicContract}
            etherlinkPublicContract={etherlinkPublicContract}
            account={account}
            isAdmin={isAdmin}
            refreshGallery={refreshGallery}
            confirmedAdminMints={confirmedAdminMints}
            view={view}
            accountCreated={accountCreated}
            sogniClient={sogniClient}
            isSogniLoggedIn={isSogniLoggedIn}
            handleSogniLogin={handleSogniLogin}
            sogniUsername={sogniUsername}
            onRequireLogin={requestSogniReauth}
            signer={signer}
            onBridgeStateChange={setBridgeInProgress}
          />
        ) : view === 'myEarnings' ? (
          <MyEarnings
            account={account}
            isSogniLoggedIn={isSogniLoggedIn}
            sogniBalance={sogniBalance}
            sogniManagerBalance={sogniManagerBalance}
            showBackupModal={showBackupModal}
            setShowBackupModal={setShowBackupModal}
            showPrivateKey={showPrivateKey}
            privateKeyToShow={privateKeyToShow}
            backupPassword={backupPassword}
            setBackupPassword={setBackupPassword}
            handleBackupPrivateKeyEvent={handleBackupPrivateKeyEvent}
            setShowPrivateKey={setShowPrivateKey}
            setPrivateKeyToShow={setPrivateKeyToShow}
            showDepositModal={showDepositModal}
            setShowDepositModal={setShowDepositModal}
            depositAmount={depositAmount}
            setDepositAmount={setDepositAmount}
            transactionStatus={transactionStatus}
            transactionHash={transactionHash}
            handleConfirmDeposit={handleConfirmDeposit}
            showWithdrawModal={showWithdrawModal}
            setShowWithdrawModal={setShowWithdrawModal}
            withdrawAmount={withdrawAmount}
            setWithdrawAmount={setWithdrawAmount}
            handleConfirmWithdraw={handleConfirmWithdraw}
            showSendModal={showSendModal}
            setShowSendModal={setShowSendModal}
            sendRecipient={sendRecipient}
            setSendRecipient={setSendRecipient}
            sendAmount={sendAmount}
            setSendAmount={setSendAmount}
            sendStatus={sendStatus}
            sendTxHash={sendTxHash}
            handleSendTsogni={handleSendTsogni}
            sending={sending}
            openDepositModal={openDepositModal}
            openWithdrawModal={openWithdrawModal}
            openSendModal={openSendModal}
            sogniClient={sogniClient}
            workerUnclaimedBalance={workerUnclaimedBalance}
            openWorkerClaimModal={openWorkerClaimModal}
            showWorkerWithdrawModal={showWorkerWithdrawModal}
            setShowWorkerWithdrawModal={setShowWorkerWithdrawModal}
            workerWithdrawAmount={workerWithdrawAmount}
            handleConfirmWorkerWithdraw={handleConfirmWorkerWithdraw}
            setWorkerUnclaimedBalance={setWorkerUnclaimedBalance}
          />
        ) : view === 'fastworkers' ? (
          <FastWorkersMovedView account={account} />
        ) : view === 'docs' ? (
          <DocsPage />
        ) : null}
      </Container>

      {/* Sogni login modals */}
      <SogniAuthModals
        showSogniLoginModal={showSogniLoginModal}
        setShowSogniLoginModal={setShowSogniLoginModal}
        sogniUsername={sogniUsername}
        setSogniUsername={setSogniUsername}
        sogniPassword={sogniPassword}
        setSogniPassword={setSogniPassword}
        sogniErrorMessage={sogniErrorMessage}
        handleSogniLoginSubmit={handleSogniLoginSubmit}
        sogniClientReady={sogniClientReady}
        accountCreated={accountCreated}
        showSogniReauthModal={showSogniReauthModal}
        handleSogniReauthSubmit={handleSogniReauthSubmit}
        sogniReauthPassword={sogniReauthPassword}
        setSogniReauthPassword={setSogniReauthPassword}
        handleSogniReauthCancel={handleSogniReauthCancel}
      />
    </Container>
  );
};

export default App;
